import React, { useState, useRef, useEffect } from 'react';
import { Camera, X, User, Ruler, Weight, Calendar, Activity, Flame, Settings } from 'lucide-react';
import './FitnessTool.css';

const FitnessTracker = ({ 
  firestoreService,
  userName,
  AZURE_OPENAI_ENDPOINT,
  AZURE_OPENAI_API_KEY,
  AZURE_OPENAI_DEPLOYMENT
}) => {
  // ============================================
  // API KEYS - GET FROM ENVIRONMENT VARIABLES
  // ============================================
  // IMPORTANT: Add these to your .env file:
  // REACT_APP_GOOGLE_VISION_API_KEY=your_google_vision_key
  // REACT_APP_AZURE_OPENAI_ENDPOINT=your_azure_endpoint
  // REACT_APP_AZURE_OPENAI_API_KEY=your_azure_key
  // REACT_APP_AZURE_OPENAI_DEPLOYMENT=your_deployment
  
  const GOOGLE_VISION_API_KEY = process.env.REACT_APP_GOOGLE_VISION_API_KEY;
  
  // Validate API key exists
  useEffect(() => {
    if (!GOOGLE_VISION_API_KEY) {
      console.warn('⚠️ Google Vision API key not found in environment variables');
      console.warn('Add REACT_APP_GOOGLE_VISION_API_KEY to your .env file');
    }
    if (!AZURE_OPENAI_API_KEY) {
      console.warn('⚠️ Azure OpenAI API key not found');
    }
  }, []);

  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [activeTab, setActiveTab] = useState('scanner');
  const [userProfile, setUserProfile] = useState({
    height: '',
    weight: '',
    age: '',
    gender: '',
    goal: '',
    activityLevel: ''
  });
  const [capturedImage, setCapturedImage] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [foodAnalysis, setFoodAnalysis] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [tdee, setTdee] = useState(null);
  const [calorieGoal, setCalorieGoal] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [uploadMode, setUploadMode] = useState('camera');
  const [showActivityPopup, setShowActivityPopup] = useState(false);

  const fileInputRef = useRef(null);

  // ============================================
  // ACTIVITY LEVEL SELECTOR COMPONENT
  // ============================================
  const ActivityLevelSelector = () => {
    const levels = [
      { value: 'sedentary', label: 'Sedentary', desc: 'Little to no exercise' },
      { value: 'light', label: 'Light', desc: '1-3 days per week' },
      { value: 'moderate', label: 'Moderate', desc: '3-5 days per week' },
      { value: 'active', label: 'Active', desc: '6-7 days per week' },
      { value: 'veryActive', label: 'Very Active', desc: 'Twice per day' }
    ];

    return (
      <>
        <button 
          className="activity-trigger-btn"
          onClick={() => setShowActivityPopup(true)}
        >
          <span>{userProfile.activityLevel ? levels.find(l => l.value === userProfile.activityLevel)?.label : 'Select activity level'}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>

        {showActivityPopup && (
          <div className="activity-popup-overlay" onClick={() => setShowActivityPopup(false)}>
            <div className="activity-popup" onClick={(e) => e.stopPropagation()}>
              <div className="activity-popup-header">
                <h3>Select Activity Level</h3>
                <button onClick={() => setShowActivityPopup(false)} className="activity-close">
                  <X size={20} />
                </button>
              </div>
              <div className="activity-options">
                {levels.map((level) => (
                  <button
                    key={level.value}
                    className={`activity-option-card ${userProfile.activityLevel === level.value ? 'selected' : ''}`}
                    onClick={() => {
                      setUserProfile({...userProfile, activityLevel: level.value});
                      setShowActivityPopup(false);
                    }}
                  >
                    <div className="activity-option-content">
                      <div className="activity-option-label">{level.label}</div>
                      <div className="activity-option-desc">{level.desc}</div>
                    </div>
                    {userProfile.activityLevel === level.value && (
                      <div className="activity-check">✓</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // ============================================
  // CALCULATE TDEE WHEN PROFILE CHANGES
  // ============================================
  useEffect(() => {
    if (userProfile.height && userProfile.weight && userProfile.age && userProfile.gender && userProfile.activityLevel && userProfile.goal) {
      calculateTDEE(userProfile);
    }
  }, [userProfile]);

  const calculateTDEE = (profile) => {
    let bmr;
    if (profile.gender === 'male') {
      bmr = 10 * parseFloat(profile.weight) + 6.25 * parseFloat(profile.height) - 5 * parseFloat(profile.age) + 5;
    } else {
      bmr = 10 * parseFloat(profile.weight) + 6.25 * parseFloat(profile.height) - 5 * parseFloat(profile.age) - 161;
    }

    const activityMultipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      veryActive: 1.9
    };

    const multiplier = activityMultipliers[profile.activityLevel] || 1.55;
    const calculatedTdee = Math.round(bmr * multiplier);
    setTdee(calculatedTdee);

    let goal;
    switch (profile.goal) {
      case 'lose':
        goal = calculatedTdee - 500;
        break;
      case 'gain':
        goal = calculatedTdee + 500;
        break;
      default:
        goal = calculatedTdee;
    }
    setCalorieGoal(Math.round(goal));
  };

  // ============================================
  // HANDLE IMAGE CAPTURE & FOOD ANALYSIS
  // ============================================
  const handleImageCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate API key before starting
    if (!GOOGLE_VISION_API_KEY) {
      alert('❌ Google Vision API key not configured. Please add REACT_APP_GOOGLE_VISION_API_KEY to your .env file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64Image = event.target.result.split(',')[1];
      setCapturedImage(event.target.result);
      
      setIsScanning(true);
      setScanProgress(0);

      try {
        // ============================================
        // STEP 1: GOOGLE VISION - Detect what's in the image
        // ============================================
        console.log('🔍 Step 1: Google Vision analyzing image...');
        
        const visionResponse = await fetch(
          `https://vision.googleapis.com/v1/images:annotate?key=${GOOGLE_VISION_API_KEY}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              requests: [{
                image: { content: base64Image },
                features: [
                  { type: 'LABEL_DETECTION', maxResults: 20 },
                  { type: 'WEB_DETECTION', maxResults: 15 },
                  { type: 'OBJECT_LOCALIZATION', maxResults: 10 }
                ]
              }]
            })
          }
        );

        if (!visionResponse.ok) {
          throw new Error(`Google Vision API failed: ${visionResponse.status} ${visionResponse.statusText}`);
        }

        const visionData = await visionResponse.json();
        setScanProgress(30);
        
        // Extract ALL detected items
        const labels = visionData.responses[0]?.labelAnnotations || [];
        const webEntities = visionData.responses[0]?.webDetection?.webEntities || [];
        const objects = visionData.responses[0]?.localizedObjectAnnotations || [];
        
        // Combine all detections
        const allDetections = [
          ...labels.map(l => ({ name: l.description, score: l.score })),
          ...webEntities.map(e => ({ name: e.description, score: e.score || 0.5 })),
          ...objects.map(o => ({ name: o.name, score: o.score }))
        ].filter(item => item.name);

        // Sort by confidence score
        allDetections.sort((a, b) => b.score - a.score);

        console.log('🔍 Google Vision detected:', allDetections.map(d => d.name).slice(0, 10));

        // ============================================
        // STEP 2: AZURE AI - Intelligent food identification
        // ============================================
        console.log('🤖 Step 2: Azure AI identifying foods...');
        setScanProgress(50);

        const detectedItems = allDetections.map(d => d.name).slice(0, 15).join(', ');

        const identificationPrompt = `You are a food identification expert. Analyze these detected items from a food image and identify the ACTUAL FOODS present.

DETECTED ITEMS: ${detectedItems}

Your task:
1. Identify 2-4 specific food items that are likely in this meal
2. Be specific with food names (e.g., "Chicken Caesar Salad" not just "Salad")
3. Consider restaurant portions and typical meal combinations
4. Include side dishes if detected

Return ONLY valid JSON:
{
  "foods": [
    {
      "name": "Specific Food Name",
      "category": "main/side/drink/dessert",
      "portionSize": "typical restaurant serving description"
    }
  ],
  "mealType": "breakfast/lunch/dinner/snack",
  "confidence": 0.85
}`;

        const identificationResponse = await fetch(
          `${AZURE_OPENAI_ENDPOINT}/openai/deployments/${AZURE_OPENAI_DEPLOYMENT}/chat/completions?api-version=2024-02-15-preview`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'api-key': AZURE_OPENAI_API_KEY
            },
            body: JSON.stringify({
              messages: [
                { role: 'system', content: identificationPrompt },
                { role: 'user', content: 'Identify the foods in this meal.' }
              ],
              temperature: 0.4,
              max_tokens: 500,
              response_format: { type: "json_object" }
            })
          }
        );

        if (!identificationResponse.ok) {
          throw new Error('Azure food identification failed');
        }

        const identificationData = await identificationResponse.json();
        const identifiedFoods = JSON.parse(identificationData.choices[0].message.content);

        console.log('🤖 Azure identified foods:', identifiedFoods);

        // ============================================
        // STEP 3: AZURE AI - Calculate nutrition
        // ============================================
        console.log('📊 Step 3: Azure AI calculating nutrition...');
        setScanProgress(75);

        const foodsList = identifiedFoods.foods.map(f => 
          `${f.name} (${f.category}, ${f.portionSize})`
        ).join('\n');

        const nutritionPrompt = `You are a professional nutritionist. Calculate realistic nutrition values for this meal.

IDENTIFIED FOODS:
${foodsList}

MEAL TYPE: ${identifiedFoods.mealType}

Calculate nutrition for EACH food item based on typical restaurant/homemade portions:

Return ONLY valid JSON:
{
  "foods": [
    {
      "name": "Food Name",
      "calories": 450,
      "protein": 25,
      "carbs": 45,
      "fat": 18,
      "servingSize": "1 serving description"
    }
  ],
  "totalCalories": 850,
  "totalProtein": 45,
  "totalCarbs": 90,
  "totalFat": 35,
  "confidence": 0.88
}`;

        const nutritionResponse = await fetch(
          `${AZURE_OPENAI_ENDPOINT}/openai/deployments/${AZURE_OPENAI_DEPLOYMENT}/chat/completions?api-version=2024-02-15-preview`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'api-key': AZURE_OPENAI_API_KEY
            },
            body: JSON.stringify({
              messages: [
                { role: 'system', content: nutritionPrompt },
                { role: 'user', content: 'Calculate nutrition for these foods.' }
              ],
              temperature: 0.3,
              max_tokens: 1000,
              response_format: { type: "json_object" }
            })
          }
        );

        if (!nutritionResponse.ok) {
          throw new Error('Azure nutrition calculation failed');
        }

        const nutritionData = await nutritionResponse.json();
        const nutrition = JSON.parse(nutritionData.choices[0].message.content);

        console.log('📊 Azure nutrition:', nutrition);

        setScanProgress(100);

        // ============================================
        // STEP 4: Show results
        // ============================================
        setTimeout(() => {
          setIsScanning(false);
          setFoodAnalysis({
            totalCalories: nutrition.totalCalories,
            protein: nutrition.totalProtein,
            carbs: nutrition.totalCarbs,
            fat: nutrition.totalFat,
            confidence: Math.round((identifiedFoods.confidence + nutrition.confidence) / 2 * 100),
            foods: nutrition.foods
          });
          setShowResultsModal(true);
        }, 500);

      } catch (error) {
        console.error('❌ Food analysis error:', error);
        setIsScanning(false);
        alert(`Analysis failed: ${error.message}\n\nPlease check:\n1. Google Vision API key is valid (REACT_APP_GOOGLE_VISION_API_KEY in .env)\n2. Azure OpenAI credentials are correct\n3. You have internet connection`);
      }
    };
    reader.readAsDataURL(file);
  };

  // ============================================
  // COMPLETION PERCENTAGE HELPER
  // ============================================
  const getCompletionPercentage = (field) => {
    if (!userProfile[field]) return 0;
    return 100;
  };

  // ============================================
  // CUSTOM ICONS - MARS & VENUS
  // ============================================
  const MarsIcon = ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="15" r="6"/>
      <line x1="13.5" y1="10.5" x2="20" y2="4"/>
      <line x1="16" y1="4" x2="20" y2="4"/>
      <line x1="20" y1="4" x2="20" y2="8"/>
    </svg>
  );

  const VenusIcon = ({ size, color }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="9" r="6"/>
      <line x1="12" y1="15" x2="12" y2="23"/>
      <line x1="9" y1="20" x2="15" y2="20"/>
    </svg>
  );

  // ============================================
  // GEAR NUTRITION WHEEL - RESULTS MODAL
  // ============================================
  const GearNutritionWheel = ({ value, max, color, label, unit }) => {
    const percentage = Math.min((value / max) * 100, 100);
    const radius = 55;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="gear-nutrition-card">
        <div className="gear-nutrition-circle">
          <svg width="150" height="150" className="gear-svg">
            <defs>
              <filter id={`glow-${label}`}>
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <circle cx="75" cy="75" r={radius} fill="none" stroke="#f0f0f0" strokeWidth="10" />
            <circle 
              cx="75" 
              cy="75" 
              r={radius} 
              fill="none" 
              stroke={color} 
              strokeWidth="10" 
              strokeDasharray={circumference} 
              strokeDashoffset={offset} 
              strokeLinecap="round" 
              transform="rotate(-90 75 75)"
              filter={`url(#glow-${label})`}
              className="gear-nutrition-progress"
            />
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30) * (Math.PI / 180);
              const x1 = 75 + (radius - 5) * Math.cos(angle);
              const y1 = 75 + (radius - 5) * Math.sin(angle);
              const x2 = 75 + (radius + 8) * Math.cos(angle);
              const y2 = 75 + (radius + 8) * Math.sin(angle);
              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="#d0d0d0"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              );
            })}
          </svg>
          <div className="gear-nutrition-center">
            <div className="gear-nutrition-value">{value}</div>
            <div className="gear-nutrition-unit">{unit}</div>
          </div>
        </div>
        <div className="gear-nutrition-label">{label}</div>
      </div>
    );
  };

  // ============================================
  // GENDER SELECTOR
  // ============================================
  const GenderSelector = () => (
    <div className="gender-glass-container">
      <div className="gender-slider" style={{ left: userProfile.gender === 'male' ? '4px' : userProfile.gender === 'female' ? 'calc(50% + 2px)' : '-100%' }}></div>
      <button className={`gender-option ${userProfile.gender === 'male' ? 'active' : ''}`} onClick={() => setUserProfile({...userProfile, gender: 'male'})}>
        <MarsIcon size={22} color="#7ed6ffff" />
        <span>Male</span>
      </button>
      <button className={`gender-option ${userProfile.gender === 'female' ? 'active' : ''}`} onClick={() => setUserProfile({...userProfile, gender: 'female'})}>
        <VenusIcon size={22} color="pink" />
        <span>Female</span>
      </button>
    </div>
  );

  // ============================================
  // GEAR STAT CARD - PROFILE VIEW
  // ============================================
  const GearStat = ({ icon: Icon, label, value, field, color }) => {
    const percentage = getCompletionPercentage(field);
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className={`gear-stat-card ${!value ? 'gear-disabled' : ''}`}>
        <div className="gear-stat-circle">
          <svg width="120" height="120" className="gear-progress">
            <circle cx="60" cy="60" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="8" />
            <circle cx="60" cy="60" r={radius} fill="none" stroke={value ? color : '#d1d5db'} strokeWidth="8" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" transform="rotate(-90 60 60)" className="gear-progress-fill" />
          </svg>
          <div className="gear-center">
            <div className="gear-icon-wrapper">
              <Icon size={28} className="gear-icon" style={{ color: value ? color : '#9ca3af' }} />
            </div>
          </div>
        </div>
        <div className="gear-stat-content">
          <div className="gear-stat-label"><span>{label}</span></div>
          <div className="gear-stat-value">{value || '--'}</div>
          <div className="gear-stat-unit">{value ? (field === 'height' ? 'cm' : field === 'weight' ? 'kg' : 'years') : 'not set'}</div>
        </div>
        <button className="gear-edit-btn" onClick={() => setEditingField(field)}>
          <span>Edit</span>
        </button>
      </div>
    );
  };

  // ============================================
  // EDIT POPUP
  // ============================================
  const EditPopup = ({ field, value, onSave, onClose }) => (
    <div className="edit-popup-overlay" onClick={onClose}>
      <div className="edit-popup" onClick={(e) => e.stopPropagation()}>
        <h3 className="edit-popup-title">Edit {field}</h3>
        <input type="number" defaultValue={value} className="edit-popup-input" placeholder={`Enter ${field}`} id="popup-input" autoFocus />
        <div className="edit-popup-actions">
          <button onClick={onClose} className="popup-btn-cancel">Cancel</button>
          <button onClick={() => { const val = document.getElementById('popup-input').value; onSave(val); onClose(); }} className="popup-btn-save">Save</button>
        </div>
      </div>
    </div>
  );

  // ============================================
  // RESULTS MODAL WITH NUTRITION WHEELS
  // ============================================
  const ResultsModal = () => {
    if (!showResultsModal || !foodAnalysis) return null;

    return (
      <div className="results-modal-overlay" onClick={() => setShowResultsModal(false)}>
        <div className="results-modal" onClick={(e) => e.stopPropagation()}>
          <button className="results-modal-close" onClick={() => setShowResultsModal(false)}>
            <X size={24} />
          </button>
          
          <h2 className="results-modal-title">Nutrition Analysis</h2>
          
          <div className="gear-nutrition-grid">
            <GearNutritionWheel 
              value={foodAnalysis.totalCalories} 
              max={2500} 
              color="#ff6b6b" 
              label="Calories" 
              unit="cal"
            />
            <GearNutritionWheel 
              value={foodAnalysis.protein} 
              max={200} 
              color="#4ecdc4" 
              label="Protein" 
              unit="g"
            />
            <GearNutritionWheel 
              value={foodAnalysis.carbs} 
              max={300} 
              color="#f7b731" 
              label="Carbs" 
              unit="g"
            />
            <GearNutritionWheel 
              value={foodAnalysis.fat} 
              max={100} 
              color="#a29bfe" 
              label="Fats" 
              unit="g"
            />
          </div>

          <div className="foods-detected-section">
            <h3 className="foods-detected-title">Foods Detected</h3>
            <div className="foods-detected-list">
              {foodAnalysis.foods.map((food, idx) => (
                <div key={idx} className="food-detected-item">
                  <div className="food-detected-name">{food.name}</div>
                  <div className="food-detected-calories">{food.calories} cal</div>
                  <div className="food-detected-macros">
                    <span>P: {food.protein}g</span>
                    <span>C: {food.carbs}g</span>
                    <span>F: {food.fat}g</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button className="results-modal-btn" onClick={() => setShowResultsModal(false)}>
            Add to Log
          </button>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER PROFILE VIEW
  // ============================================
  const renderProfileView = () => (
    <div className="ft-content">
      <div className="ft-instruction-card">
        <h3 className="ft-instruction-title">Setup Your Profile</h3>
        <p className="ft-instruction-text">Fill in your details below to get personalized calorie goals. Watch the progress rings fill up as you complete each field!</p>
      </div>

      <div className="gear-stats-grid">
        <GearStat icon={Ruler} label="Height" value={userProfile.height} field="height" color="#10b981" />
        <GearStat icon={Weight} label="Weight" value={userProfile.weight} field="weight" color="#f59e0b" />
        <GearStat icon={Calendar} label="Age" value={userProfile.age} field="age" color="#8b5cf6" />
      </div>

      <div className="ft-form-card">
        <div className="ft-form-group">
          <label className="ft-form-label">Gender</label>
          <GenderSelector />
        </div>

        <div className="ft-form-group">
          <label className="ft-form-label">Goal</label>
          <div className="ft-btn-group">
            {['lose', 'maintain', 'gain'].map(g => (
              <button key={g} onClick={() => setUserProfile({...userProfile, goal: g})} className={`ft-btn-option ${userProfile.goal === g ? 'ft-btn-option-active' : ''}`}>
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="ft-form-group">
          <label className="ft-form-label">Activity Level</label>
          <ActivityLevelSelector />
        </div>
      </div>

      {calorieGoal && (
        <div className="ft-results-card">
          <div className="ft-result-item">
            <Flame size={24} className="ft-result-icon" />
            <div className="ft-result-content">
              <div className="ft-result-label">Daily Calorie Goal</div>
              <div className="ft-result-value">{calorieGoal}</div>
            </div>
          </div>
          <div className="ft-result-item">
            <Activity size={24} className="ft-result-icon" />
            <div className="ft-result-content">
              <div className="ft-result-label">Maintenance (TDEE)</div>
              <div className="ft-result-value">{tdee}</div>
            </div>
          </div>
        </div>
      )}

      {editingField && (
        <EditPopup field={editingField} value={userProfile[editingField]} onSave={(val) => setUserProfile({...userProfile, [editingField]: val})} onClose={() => setEditingField(null)} />
      )}
    </div>
  );

  // ============================================
  // RENDER SCANNER VIEW
  // ============================================
  const renderScannerView = () => (
    <div className="ft-content">
      {!capturedImage ? (
        <div className="ft-camera-container">
          <div className="ft-camera-frame">
            <div className="ft-camera-corner ft-corner-tl"></div>
            <div className="ft-camera-corner ft-corner-tr"></div>
            <div className="ft-camera-corner ft-corner-bl"></div>
            <div className="ft-camera-corner ft-corner-br"></div>
            <div className="ft-camera-content">
              <Camera size={64} className="ft-camera-icon" />
              <h3 className="ft-camera-title">{uploadMode === 'camera' ? 'Scan Your Meal' : 'Upload Your Photo'}</h3>
              <p className="ft-camera-subtitle">{uploadMode === 'camera' ? 'Point camera at food for instant analysis' : 'Select an image from your gallery'}</p>
            </div>
          </div>

          <div className="upload-mode-slider">
            <div className="upload-mode-indicator" style={{ left: uploadMode === 'camera' ? '4px' : 'calc(50% + 2px)' }}></div>
            <button className={`upload-mode-btn ${uploadMode === 'camera' ? 'active' : ''}`} onClick={() => setUploadMode('camera')}>
              <Camera size={18} />
              <span>Scan Meal</span>
            </button>
            <button className={`upload-mode-btn ${uploadMode === 'upload' ? 'active' : ''}`} onClick={() => setUploadMode('upload')}>
              <User size={18} />
              <span>Upload Photo</span>
            </button>
          </div>

          <button 
            onClick={() => fileInputRef.current?.click()} 
            className="ft-capture-btn"
          >
            <Camera size={24} />
            <span>{uploadMode === 'camera' ? 'Take Photo' : 'Choose Photo'}</span>
          </button>

          <input ref={fileInputRef} type="file" accept="image/*" capture={uploadMode === 'camera' ? "environment" : undefined} onChange={handleImageCapture} style={{ display: 'none' }} />
        </div>
      ) : (
        <div className="ft-analysis-container">
          <div className="ft-image-preview">
            <img src={capturedImage} alt="Food" className="ft-preview-image" />
            
            {isScanning && (
              <div className="ft-scan-overlay-photo">
                <div className="ft-scan-line-animation"></div>
                <div className="ft-scan-bottom-text">
                  <span>Analyzing nutrition...</span>
                  <span className="ft-scan-percent">{Math.round(scanProgress)}%</span>
                </div>
              </div>
            )}

            <button 
              onClick={() => { 
                setCapturedImage(null); 
                setFoodAnalysis(null); 
                setShowResultsModal(false); 
              }} 
              className="ft-close-btn"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      <ResultsModal />
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="ft-app">
      <div className="ft-main">
        {activeTab === 'profile' && renderProfileView()}
        {activeTab === 'scanner' && renderScannerView()}
      </div>

      <div className="fitglassmorphism-nav-container" data-active={activeTab}>
        <div className="tnv-nav-slider-indicator"></div>
        <button onClick={() => setActiveTab('profile')} className={`tnv-nav-glass-tab ${activeTab === 'profile' ? 'active' : ''}`}>
          <div className="tnv-nav-icon"><Settings size={16} /></div>
          <span>Profile</span>
        </button>
        <button onClick={() => setActiveTab('scanner')} className={`tnv-nav-glass-tab ${activeTab === 'scanner' ? 'active' : ''}`}>
          <div className="tnv-nav-icon"><Camera size={16} /></div>
          <span>Scanner</span>
        </button>
      </div>
    </div>
  );
};

export default FitnessTracker;